import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useParams } from 'react-router-dom'
import { Plus, User as UserIcon } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'
import { FilterChip } from '@/components/ui'
import { cn } from '@/lib/utils.ts'
import { UNRESOLVED_EMPLOYEE_ID, type Responsible } from '../../types.ts'

interface TanssPerson {
  /** TANSS-Person-ID (als String geliefert vom zentralen Adapter). */
  id: string
  name: string
  email?: string | null
}

interface ResponsiblePickerProps {
  value: Responsible[]
  onChange: (next: Responsible[]) => void
  /** Wenn `value` leer ist und `inherited` gesetzt, werden die geerbten Chips hellgrau angezeigt. */
  inherited?: { list: Responsible[]; sourceLabel: string } | null
  /** Maximal zulaessige Anzahl eigener Verantwortlicher. Default 3. */
  maxCount?: number
  disabled?: boolean
  /** Optionaler Platzhalter, wenn weder eigene noch geerbte Verantwortliche da sind. */
  placeholder?: string
}

/**
 * Auswahl der Verantwortlichen eines Share/Drive/Folder als Tag-Liste mit
 * TANSS-Mitarbeiter-Dropdown — laedt die **Personen des Kunden** (nicht die
 * internen Techniker). `customerId` kommt aus dem Route-Param und entspricht
 * der TANSS-Company-ID.
 *
 * Pro Ordner bis zu `maxCount` (Default 3) in freier Reihenfolge
 * (Position 1 = Haupt-Verantwortlicher). Reihenfolge per Drag&Drop aenderbar;
 * Chips beim Hover mit X zum Entfernen.
 *
 * Das Dropdown-Popup wird via Portal in `document.body` gerendert, damit es
 * nicht vom `overflow-x-auto`-Container der Matrix abgeschnitten wird.
 *
 * Wenn `value` leer ist und `inherited` geliefert wird, werden die Chips
 * in hellgrau/dashed als „geerbt" gerendert (nicht editierbar). Klick auf
 * das Plus-Symbol ueberschreibt die Vererbung mit eigener Auswahl.
 */
export function ResponsiblePicker({
  value,
  onChange,
  inherited,
  maxCount = 3,
  disabled,
  placeholder = 'Verantwortliche wählen …',
}: ResponsiblePickerProps) {
  const { customerId } = useParams<{ customerId: string }>()
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [dragIndex, setDragIndex] = useState<number | null>(null)
  const [popupPos, setPopupPos] = useState<{ top: number; left: number } | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const popupRef = useRef<HTMLDivElement | null>(null)

  // Zentraler SysMan→TANSS-Adapter (`/api/tanss/companies/:id/persons`)
  // liefert `{ data: TanssPerson[], total: number }` — im Gegensatz zum
  // deprecated Ticket-Endpoint, der ein rohes Array lieferte.
  const { data: persons = [], isLoading: personsLoading, error: personsError } = useQuery<TanssPerson[]>({
    queryKey: ['tanss', 'company-persons', customerId],
    queryFn: async () => {
      const res = await apiClient.get<{ data: TanssPerson[] }>(`/tanss/companies/${customerId}/persons`)
      return res.data.data ?? []
    },
    staleTime: 60_000,
    enabled: dropdownOpen && !!customerId,
    retry: false,
  })

  // Ausserhalb-Klick schliesst (prueft Container + Portal-Popup)
  useEffect(() => {
    if (!dropdownOpen) return
    function onDocClick(e: MouseEvent) {
      const target = e.target as Node
      if (containerRef.current?.contains(target)) return
      if (popupRef.current?.contains(target)) return
      setDropdownOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [dropdownOpen])

  // Popup-Position berechnen (fixed, unterhalb Trigger; Fallback oben bei Viewport-Unterkante)
  useLayoutEffect(() => {
    if (!dropdownOpen || !triggerRef.current) {
      setPopupPos(null)
      return
    }
    const rect = triggerRef.current.getBoundingClientRect()
    const popupWidth = 256 // w-64
    const popupMaxHeight = 320 // Suchfeld + max-h-64 Liste
    const vw = window.innerWidth
    const vh = window.innerHeight
    let top = rect.bottom + 4
    if (top + popupMaxHeight > vh - 8) {
      top = Math.max(8, rect.top - popupMaxHeight - 4)
    }
    let left = rect.left
    if (left + popupWidth > vw - 8) {
      left = Math.max(8, vw - popupWidth - 8)
    }
    setPopupPos({ top, left })
  }, [dropdownOpen])

  // Gefilterte Personen-Liste: Suchtreffer + nicht bereits in value.
  // IDs vom Adapter kommen als String, Responsible.employeeId ist Number —
  // Vergleich normalisiert ueber String-Konvertierung.
  const filteredPersons = useMemo(() => {
    const alreadyIds = new Set(value.map((r) => String(r.employeeId)))
    const q = search.trim().toLowerCase()
    return persons
      .filter((p) => !alreadyIds.has(String(p.id)))
      .filter((p) => !q || p.name.toLowerCase().includes(q) || (p.email ?? '').toLowerCase().includes(q))
      .slice(0, 30)
  }, [persons, search, value])

  const isShowingInherited = value.length === 0 && !!inherited && inherited.list.length > 0
  const displayList = isShowingInherited ? inherited!.list : value
  const canAddMore = !disabled && value.length < maxCount

  function addPerson(p: TanssPerson) {
    if (value.length >= maxCount) return
    const numId = Number.parseInt(String(p.id), 10)
    onChange([...value, { employeeId: Number.isFinite(numId) ? numId : UNRESOLVED_EMPLOYEE_ID, name: p.name }])
    setSearch('')
    setDropdownOpen(false)
  }

  function removeAt(idx: number) {
    const next = value.filter((_, i) => i !== idx)
    onChange(next)
  }

  function reorder(from: number, to: number) {
    if (from === to || from < 0 || to < 0 || from >= value.length || to >= value.length) return
    const next = [...value]
    const [moved] = next.splice(from, 1)
    next.splice(to, 0, moved)
    onChange(next)
  }

  function onChipDragStart(e: React.DragEvent, index: number) {
    setDragIndex(index)
    e.dataTransfer.effectAllowed = 'move'
    // Firefox benoetigt zwingend setData, damit Drag ueberhaupt startet.
    e.dataTransfer.setData('text/plain', String(index))
  }

  function onChipDragOver(e: React.DragEvent, index: number) {
    if (dragIndex === null || dragIndex === index) return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  function onChipDrop(e: React.DragEvent, index: number) {
    e.preventDefault()
    if (dragIndex === null) return
    reorder(dragIndex, index)
    setDragIndex(null)
  }

  function onChipDragEnd() {
    setDragIndex(null)
  }

  return (
    <div
      ref={containerRef}
      className="flex flex-wrap items-center gap-1"
      title={isShowingInherited ? inherited!.sourceLabel : undefined}
    >
      {displayList.length === 0 && !canAddMore && (
        <span className="text-caption text-gray-400 italic">—</span>
      )}

      {displayList.map((resp, idx) => {
        const isUnresolved = resp.employeeId === UNRESOLVED_EMPLOYEE_ID
        const baseTitle = isUnresolved
          ? `⚠ Nicht in TANSS gemappt — bitte manuell zuordnen`
          : isShowingInherited
            ? inherited!.sourceLabel
            : `TANSS-Person-ID: ${resp.employeeId}`
        return (
          <FilterChip
            key={`${resp.employeeId}-${idx}`}
            draggable={!isShowingInherited && !disabled && value.length > 1}
            onDragStart={(e) => onChipDragStart(e, idx)}
            onDragOver={(e) => onChipDragOver(e, idx)}
            onDrop={(e) => onChipDrop(e, idx)}
            onDragEnd={onChipDragEnd}
            leadingIcon={<UserIcon />}
            onRemove={isShowingInherited || disabled ? undefined : () => removeAt(idx)}
            title={baseTitle}
            className={cn(
              'cursor-default',
              isShowingInherited && 'bg-gray-50 text-gray-500 border-dashed',
              isUnresolved && !isShowingInherited && 'border-amber-300 bg-amber-50 text-amber-700',
              !isShowingInherited && value.length > 1 && 'cursor-grab active:cursor-grabbing',
              dragIndex === idx && 'opacity-40',
            )}
          >
            {resp.name}
          </FilterChip>
        )
      })}

      {canAddMore && (
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setDropdownOpen((prev) => !prev)}
          className={cn(
            'inline-flex items-center gap-1 rounded-md border border-dashed border-gray-300 px-1.5 py-1 text-caption text-gray-500 hover:border-brand-400 hover:text-brand-600 transition',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400',
          )}
          aria-label="Verantwortlichen hinzufügen"
          title={value.length === 0 && !isShowingInherited ? placeholder : 'Weiteren Verantwortlichen hinzufügen'}
        >
          <Plus className="h-3 w-3" />
          {displayList.length === 0 && <span>{placeholder}</span>}
        </button>
      )}

      {dropdownOpen && canAddMore && popupPos && createPortal(
        <div
          ref={popupRef}
          style={{ position: 'fixed', top: popupPos.top, left: popupPos.left, width: 256 }}
          className="z-popover rounded-md border border-gray-200 bg-white shadow-popover"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="p-2 border-b border-gray-100">
            <input
              type="text"
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Mitarbeiter des Kunden suchen…"
              className="w-full px-2 py-1 text-caption border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
          </div>
          <div className="max-h-64 overflow-y-auto">
            {!customerId && (
              <div className="px-3 py-2 text-caption text-gray-400">
                Kein Kunden-Kontext — Mitarbeiter können nicht geladen werden.
              </div>
            )}
            {customerId && personsLoading && (
              <div className="px-3 py-2 text-caption text-gray-400">Lade Mitarbeiter…</div>
            )}
            {customerId && !personsLoading && personsError && (
              <details className="px-3 py-2 text-caption text-red-700">
                <summary className="cursor-pointer">
                  ⚠ Mitarbeiter konnten nicht aus TANSS geladen werden.
                </summary>
                <div className="mt-1 text-[10px] font-mono whitespace-pre-wrap text-red-600">
                  {(() => {
                    const ax = personsError as {
                      response?: { status?: number; data?: { error?: string; message?: string } }
                      message?: string
                    }
                    const status = ax.response?.status
                    const msg = ax.response?.data?.error ?? ax.response?.data?.message ?? ax.message ?? String(personsError)
                    return `${status ? `HTTP ${status}: ` : ''}${msg}`
                  })()}
                </div>
              </details>
            )}
            {customerId && !personsLoading && !personsError && filteredPersons.length === 0 && (
              <div className="px-3 py-2 text-caption text-gray-400">
                {persons.length === 0
                  ? 'Keine Mitarbeiter für diesen Kunden in TANSS hinterlegt.'
                  : 'Keine Treffer.'}
              </div>
            )}
            {filteredPersons.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => addPerson(p)}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-caption hover:bg-brand-50"
              >
                <UserIcon className="h-3 w-3 text-gray-400 flex-shrink-0" />
                <span className="truncate">{p.name}</span>
                {p.email && (
                  <span className="ml-auto text-[10px] text-gray-400 truncate max-w-[120px]">{p.email}</span>
                )}
              </button>
            ))}
          </div>
        </div>,
        document.body,
      )}
    </div>
  )
}
